import { parseWithZod } from '@conform-to/zod'
import {
	json,
	redirect,
	type ActionFunction,
	type LoaderFunctionArgs,
	unstable_parseMultipartFormData,
	unstable_createMemoryUploadHandler,
} from '@remix-run/node'
import {
	Link,
	isRouteErrorResponse,
	useLoaderData,
	useRouteError,
	useSearchParams,
} from '@remix-run/react'
import { z } from 'zod'
import AnonymousMemberCta from '#app/components/ui/anonymous-member-cta.js'
import { BorderBeam } from '#app/components/ui/border-beam.js'
import { Button } from '#app/components/ui/button'
import { Dialog, DialogContent, DialogTrigger } from '#app/components/ui/dialog'
import { Label } from '#app/components/ui/label.js'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '#app/components/ui/select'
import { getUserId, requireUserId } from '#app/utils/auth.server'
import { prisma } from '#app/utils/db.server'
import { client } from '#app/utils/inngest.server.js'
import { getTargetImgSrc } from '#app/utils/misc.js'
import {
	requireUserWithPermission,
	userHasPermission,
} from '#app/utils/permissions.server.js'
import { generateTargetRef, TargetProcessingStatus } from '#app/utils/target.js'
import { NewTargetForm } from './__edit-target-form'

const isDevelopment = process.env.NODE_ENV === 'development'

const TargetRandomSchema = z.object({
	intent: z.literal('from-random'),
})

const MAX_SIZE = 1024 * 1024 * 10 // 10MB

const TargetImageSchema = z.object({
	intent: z.literal('from-photo'),
	photoFile: z
		.instanceof(File)
		.refine(file => file.size > 0, 'Image is required')
		.refine(file => file.size <= MAX_SIZE, 'Image size must be less than 10MB'),
})

const CombinedTargetSchema = TargetImageSchema.or(TargetRandomSchema)

export const loader = async ({ request }: LoaderFunctionArgs) => {
	const userId = await getUserId(request)

	const hasPermissionToCreateTarget = isDevelopment
		? true
		: userId
			? await userHasPermission(userId, 'create:target:any')
			: false

	const url = new URL(request.url)
	const filter = url.searchParams.get('filter') || 'blinded'

	let whereClause = {}
	if (filter === 'blinded') {
		whereClause = {
			TargetReveal: {
				none: {
					userId,
				},
			},
		}
	} else if (filter === 'revealed' && userId) {
		whereClause = {
			TargetReveal: {
				some: {
					userId,
				},
			},
		}
	}

	if (userId) {
		const targets = await prisma.target.findMany({
			where: {
				// processingStatus: TargetProcessingStatus.COMPLETE,
				...whereClause,
			},
			orderBy: {
				createdAt: 'desc',
			},
			include: {
				TargetImage: true,
				TargetReveal: {
					where: {
						userId,
					},
				},
			},
		})

		return json({
			userId,
			targets:
				filter === 'all'
					? targets
					: filter === 'revealed'
						? targets.filter(t => t.TargetReveal.length > 0)
						: targets.slice(0, 1),
			demoTarget: null,
			filter,
			hasPermissionToCreateTarget,
		})
	} else {
		const anonTargets = await prisma.target.findMany({
			where: {
				// processingStatus: TargetProcessingStatus.COMPLETE,
			},
			orderBy: {
				createdAt: 'desc',
			},
			include: {
				TargetImage: true,
				TargetReveal: {},
			},
		})
		const randomIndex = Math.floor(Math.random() * anonTargets.length)
		const randomTarget = anonTargets[randomIndex]

		return json({
			userId: null,
			targets: null,
			demoTarget: randomTarget,
			filter,
			hasPermissionToCreateTarget,
		})
	}
}

// Action function to handle form submission
export const action: ActionFunction = async ({ request }) => {
	// Ensure the user is logged in and has permission to create target
	if (!isDevelopment) {
		await requireUserWithPermission(request, 'create:target:any')
	} else {
		await requireUserId(request)
	}

	const formData = await unstable_parseMultipartFormData(
		request,
		unstable_createMemoryUploadHandler({ maxPartSize: MAX_SIZE }),
	)
	const result = parseWithZod(formData, {
		schema: CombinedTargetSchema,
	})
	if (result.status !== 'success') {
		return json({ result: result.reply() }, { status: 400 })
	}

	const newTarget = await prisma.target.create({
		data: {
			processingStatus: TargetProcessingStatus.PROCESSING,
			description: 'Pending',
		},
	})

	// add the image to the target
	if (result.value.intent === 'from-photo') {
		const targetImage = await prisma.targetImage.create({
			data: {
				target: {
					connect: {
						id: newTarget.id,
					},
				},
				contentType: result.value.photoFile.type,
				blob: Buffer.from(await result.value.photoFile.arrayBuffer()),
			},
		})

		await client.send({
			name: 'target/image-processing',
			data: {
				targetId: newTarget.id,
				targetImageId: targetImage.id,
			},
		})
	} else {
		await client.send({
			name: 'target/request-random',
			data: {
				targetId: newTarget.id,
			},
		})
	}

	return redirect(`/targets/${newTarget.id}`)
}

const TargetRoute: React.FC = () => {
	const data = useLoaderData<typeof loader>()
	const { targets, userId, demoTarget, filter, hasPermissionToCreateTarget } =
		data
	const [, setSearchParams] = useSearchParams()

	const handleFilterChange = (value: string) => {
		setSearchParams(prev => {
			prev.set('filter', value)
			return prev
		})
	}

	return (
		<div className="container relative p-4">
			<img
				src="/img/dojo_scenes/dojo_scene_targets_1.png"
				alt="intuizen logo"
				className="w-48 object-contain"
			/>
			<div className="z-10 grid grid-cols-1 items-center gap-4 md:my-4 md:grid-cols-2">
				<div>
					<h1 className="text-4xl font-bold">Target Training</h1>
					<p className="mt-2 text-lg">
						Develop your intuizen by practicing on the target below.
					</p>
					<p>
						A new target is created daily from a random selection of real
						photographs.
					</p>
				</div>
				<div id="right-column">
					{userId ? (
						<>
							<h3 className="mb-2 text-xl font-semibold ">Create New Target</h3>
							<Dialog>
								<DialogTrigger asChild>
									<Button className="flex items-center space-x-2 bg-brand-primary-9 text-white">
										<svg
											xmlns="http://www.w3.org/2000/svg"
											className="h-5 w-5"
											viewBox="0 0 20 20"
											fill="currentColor"
										>
											<path
												fillRule="evenodd"
												d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
												clipRule="evenodd"
											/>
										</svg>
										<span>New Target</span>
									</Button>
								</DialogTrigger>
								<DialogContent>
									{hasPermissionToCreateTarget ? (
										<>
											<h2 className="mb-4 text-2xl">Create New Image Target</h2>
											<NewTargetForm />
										</>
									) : (
										<div>
											<h3 className="text-xl font-semibold">
												With great power comes great responsibility
											</h3>
											<p className="text-muted-foreground">
												You do not have permission to create a target.
											</p>
										</div>
									)}
								</DialogContent>
							</Dialog>
							<div className="my-4">
								<Label>Status</Label>
								<Select
									onValueChange={handleFilterChange}
									defaultValue={filter}
								>
									<SelectTrigger
										className="w-[180px]"
										onClick={e => e.stopPropagation()}
									>
										<SelectValue placeholder="Filter targets" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="blinded">Blinded</SelectItem>
										<SelectItem value="revealed">Revealed</SelectItem>
										<SelectItem value="all">All</SelectItem>
									</SelectContent>
								</Select>
							</div>
						</>
					) : (
						<AnonymousMemberCta />
					)}
				</div>
			</div>

			{targets && targets.length === 0 ? (
				<div className="my-8 text-center text-xl text-muted-foreground">
					{filter === 'blinded' &&
						'No blinded targets found. Come back tomorrow!'}
					{filter === 'revealed' &&
						'No revealed targets found. Start a session and reveal a target.'}
					{filter === 'all' && 'No targets found.'}
				</div>
			) : null}

			{!targets && demoTarget ? (
				<div className="relative my-4 text-xl text-muted-foreground">
					Demonstration Target
				</div>
			) : null}

			{targets && targets.length > 0 ? (
				<div className="my-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
					{targets.map(target => (
						<div key={target.id} className="col-span-1">
							<Link
								to={`/targets/${target.id}`}
								className="flex items-start gap-4 rounded-lg border bg-background p-4 shadow-sm transition-colors hover:bg-muted sm:grid sm:grid-cols-[100px_1fr]"
							>
								{target.TargetReveal.length > 0 ? (
									<img
										src={
											target.TargetImage[0]?.fullUrl ||
											getTargetImgSrc(target.TargetImage[0]?.id)
										}
										alt="Target"
										width={100}
										height={100}
										className="aspect-square max-h-32 w-full rounded-md object-cover sm:w-auto"
									/>
								) : (
									<img
										width={100}
										height={100}
										src="/img/symbol.png"
										className="blur"
										alt="target placeholder"
									/>
								)}
								<div className="w-full">
									<h3 className="mb-2 text-lg font-semibold">
										{`Target ${generateTargetRef(target.createdAt)}`}
									</h3>
									<p className="line-clamp-3 text-sm text-muted-foreground">
										{target.TargetReveal.length > 0
											? target.title || target.description
											: 'Target information is blinded.'}
									</p>
								</div>
							</Link>
						</div>
					))}
				</div>
			) : null}

			{demoTarget && !targets ? (
				<div className="my-4 flex justify-start">
					<div className="group relative flex cursor-crosshair justify-start rounded-lg border bg-background p-4 hover:bg-slate-100">
						<BorderBeam size={150} duration={15} />
						<Link
							to={`/targets/${demoTarget.id}`}
							className="p-8 text-center text-4xl group-hover:underline"
						>
							{generateTargetRef(demoTarget.createdAt) ||
								'Demonstration Target'}
						</Link>
					</div>
				</div>
			) : null}
		</div>
	)
}

export default TargetRoute

export function ErrorBoundary() {
	const error = useRouteError()

	if (isRouteErrorResponse(error) && error.status === 403) {
		return (
			<div className="flex flex-col items-center justify-center gap-6">
				<h1 className="text-h1">Unauthorized</h1>
				<p className="text-body-md text-muted-foreground">
					You don't have permission to create a target.
				</p>
				<Button asChild>
					<Link to="/targets">Back to Targets</Link>
				</Button>
			</div>
		)
	}

	return <p>An unexpected error occurred.</p>
}
